
    import {Inject, Options, Prop, Ref, Vue} from 'vue-property-decorator';
    import InputLocation from '@/common/components/location/InputLocation.vue';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {CreationFormValidation} from '@/profile-creation-wizard/components/for-creation/ProfileCreationForm';
    import {Getter} from 'vuex-class';
    import getters from '@/profile-creation-wizard/store/creation/getters';
    import mutations from '@/profile-creation-wizard/store/creation/mutations';
    import {BlockedCountryRepository} from '@/common/repositories/blocked-countries/BlockedCountryRepository';
    import type {BlockedCountriesList} from '@/common/repositories/blocked-countries/BlockedCountriesList';
    import {TipsPart} from '@/profile-creation-wizard/components/for-creation/TipsPart';
    import {eventBusProfileCreationWizard} from '@/profile-creation-wizard/common/events';
    import BottomSheetDisplayer from '@/common/ui/BottomSheetDisplayer';
    import {ProfileCreationFunnelMode} from '@/profile-creation-wizard/config/steps/ProfileCreationSteps';
    import {Address, ProfileCreationForm, WorkplacePreference} from '@/profile-creation-wizard/types';

    @Options({
        components: {
            InputLocation,
        },
    })
    export default class LocationAndWorkplace extends Vue {
        @Inject({from: DI.blockedCountriesRepository})
        blockedCountryRepository!: BlockedCountryRepository;
        @Inject({from: DI.creationFormValidation})
        creationFormValidation!: CreationFormValidation;
        @Getter(getters.creationForm)
        creationForm!: ProfileCreationForm;
        @Ref('inputLocation')
        inputLocation!: InputLocation;
        @Prop({type: String, required: true})
        funnelMode!: ProfileCreationFunnelMode;
        address?: Address;
        workPlacePreference: WorkplacePreference | null = null;
        willingnessToTravelInternationally: boolean = false;

        blockedCountriesList: BlockedCountriesList = [];
        blockedCountry = false;

        bottomSheetSelector = '#details-location-bottom-sheet';

        async mounted(): Promise<void> {
            const blockedCountries = await this.blockedCountryRepository.getAll();
            this.blockedCountriesList = blockedCountries || [];

            this.bottomSheetDisplayer = new BottomSheetDisplayer(this.bottomSheetSelector, this.$el, 'div.profile-creation-wizard');
            this.workPlacePreference = this.creationForm.workPlacePreference;
            this.willingnessToTravelInternationally = this.creationForm.willingnessToTravelInternationally;
        }

        openBottomSheet(): void {
            this.bottomSheetDisplayer.openBottomSheet();
        }

        updateAddress(newAddress: Address): void {
            this.address = newAddress;
            this.$store.commit(mutations.selectAddress, this.address);

            this.blockedCountry = this.inputLocation.isBlockedCountry;
            this.$emit('change');
        }

        updateWorkplace(newWorkplaceCustomEvent: CustomEvent): void {
            this.emitFocus();
            this.workPlacePreference = newWorkplaceCustomEvent.detail.checked ? newWorkplaceCustomEvent.detail.value : null;
            this.$store.commit(mutations.selectWorkplacePreference, this.workPlacePreference);
        }

        handleWillingnessToTravelInternationallyChange(willingnessToTravelInternationallyCustomEvent: CustomEvent): void {
            this.willingnessToTravelInternationally = willingnessToTravelInternationallyCustomEvent.detail;
            this.$store.commit(mutations.updateWillingnessToTravelInternationally, this.willingnessToTravelInternationally);
        }

        validateAddress(): void {
            // AS-931: Validate address while updating input value
            // Prevent from being able to validate form without a valid address
            this.inputLocation.validate();
            if (!this.inputLocation.inputLocation.value) {
                this.address = undefined;
                this.$store.commit(mutations.selectAddress, undefined);
            }
        }

        isWorkplaceSelected(workplaceSelected: string) {
            return this.workPlacePreference === workplaceSelected;
        }

        isWorkplaceOnSite(): boolean {
            return this.isWorkplaceSelected(WorkplacePreference.ONSITE);
        }

        isStrategyFunnelMode(): boolean {
            return this.funnelMode === ProfileCreationFunnelMode.STRATEGY;
        }

        emitFocus(): void {
            eventBusProfileCreationWizard.emit('creation:focus-changed', TipsPart.locationAndWorkplace);
        }

        get isBlockedCountry(): boolean {
            return this.blockedCountry;
        }

        get defaultLocation() {
            return this.creationForm.address;
        }

        get workplaces(): {[keys: string]: {[keys: string]: string}} {
            return {
                // PARTIALLY_ONSITE -> not available
                ONSITE: {
                    label: this.$msg('profile.creation.completion.wizard.details.location.step2.workplace.hybrid'),
                    value: 'ONSITE',
                },
                REMOTE: {
                    label: this.$msg('profile.creation.completion.wizard.details.location.step2.workplace.remote'),
                    value: 'REMOTE',
                },
            };
        }
    }
